import type { PatchActionItemInput } from './action-item-write-operations';
import { markPendingActionItemMutation } from './action-item-pending-writes';
import type { ProjectScope } from './project-scope';
import type { ProjectWriteOperation } from './write-operation';

const ACTION_ITEM_PATCH_DEBOUNCE_MS = 350;

type ScheduledPatch = {
    input: PatchActionItemInput;
    timer: ReturnType<typeof setTimeout>;
};

const scheduledPatches = new Map<string, ScheduledPatch>();

function patchKey(input: PatchActionItemInput): string {
    return `${input.scope.orgId}\0${input.scope.projectId}\0${input.actionItemId}`;
}

export function getScheduledActionItemPatchOperation(
    scope: ProjectScope,
    actionItemId: string,
): ProjectWriteOperation | undefined {
    const scheduled = scheduledPatches.get(`${scope.orgId}\0${scope.projectId}\0${actionItemId}`);
    return scheduled?.input.operation;
}

function markOptimisticPatch(input: PatchActionItemInput): void {
    const operationId = input.operation?.id;
    if (!operationId) {
        return;
    }

    markPendingActionItemMutation(input.scope, {
        operationId,
        optimistic: input.optimistic,
        prior: input.prior,
        acknowledgedItemIds: input.optimistic.items.map(item => item.id),
        acknowledgedEventIds: input.optimistic.events.map(event => event.id),
        acknowledgedEvidenceKeys: input.optimistic.reviewedEvidenceKeys,
    });
}

/**
 * Retains one operation identity per item while rapid edits settle. The
 * projection is registered immediately, before durable storage, so a remote
 * snapshot cannot erase the user's pending edit during the debounce interval.
 */
export function scheduleActionItemPatch(
    input: PatchActionItemInput,
    persist: (input: PatchActionItemInput) => Promise<unknown>,
): void {
    const key = patchKey(input);
    const existing = scheduledPatches.get(key);
    const latestInput: PatchActionItemInput = existing
        ? {
            ...input,
            operation: existing.input.operation,
            ...mergeScheduledPatch(existing.input, input),
            prior: existing.input.prior,
        }
        : input;

    if (existing) {
        clearTimeout(existing.timer);
    }

    markOptimisticPatch(latestInput);
    const timer = setTimeout(() => {
        scheduledPatches.delete(key);
        void persist(latestInput).catch(() => undefined);
    }, ACTION_ITEM_PATCH_DEBOUNCE_MS);
    scheduledPatches.set(key, { input: latestInput, timer });
}

function mergeScheduledPatch(
    existing: PatchActionItemInput,
    next: PatchActionItemInput,
): Pick<PatchActionItemInput, 'patch' | 'clearFields'> {
    const patch = { ...existing.patch, ...next.patch };
    const clearFields = new Set([...existing.clearFields, ...next.clearFields]);

    for (const field of next.clearFields) {
        delete patch[field];
    }
    for (const field of Object.keys(next.patch)) {
        if (!next.clearFields.includes(field as typeof next.clearFields[number])) {
            clearFields.delete(field as typeof next.clearFields[number]);
        }
    }

    return { patch, clearFields: [...clearFields] };
}

export function clearScheduledActionItemPatches(): void {
    for (const { timer } of scheduledPatches.values()) {
        clearTimeout(timer);
    }
    scheduledPatches.clear();
}
